/**
 * Speech.
 *
 * The honest position, stated up front: this is the device's own text-to-speech
 * engine, not a recorded human voice. It is synthetic and sounds it. What it is
 * not is nothing — and it has two properties that matter at a ground:
 *
 *   · It runs entirely offline. The voices live on the phone, so a scorer with
 *     no signal still gets the ball called out.
 *   · It costs nothing and needs no files, so it works the day you install it.
 *
 * The recorded-clip path in commentary.ts is still wired and still the better
 * answer. This is what exists in the meantime.
 *
 * Two platform facts drive the design:
 *   · Chromium returns an empty voice list until `voiceschanged` fires.
 *   · Every browser blocks speech until the user has interacted with the page,
 *     so the first call must come from inside a tap.
 */

export type Lang = "en" | "hi";

let unlocked = false;
let voices: SpeechSynthesisVoice[] = [];
let preferred: Record<Lang, string | null> = { en: null, hi: null };


/* ── which voices are worth offering ────────────────────────────────────────
   getVoices() returns everything the OS has, including Apple's novelty pack
   (Bahh, Boing, Deranged, Jester, Zarvox) and the Eloquence set (Eddy, Flo,
   Grandma, Grandpa) which are low quality and were never meant for reading
   prose. Offering them at all is the bug — a scorer should not have to know
   which of forty names is the sensible one.                                 */

/** Never offer these. Matched on the name, case-insensitively. */
const NOVELTY = [
  "albert", "bad news", "bahh", "bells", "boing", "bubbles", "cellos",
  "deranged", "good news", "goodnews", "hysterical", "jester", "junior",
  "kathy", "organ", "pipe organ", "princess", "ralph", "superstar",
  "trinoids", "whisper", "wobble", "zarvox", "zosia", "bad n",
  // Eloquence: technically speech, but robotic even by TTS standards.
  "eddy", "flo", "grandma", "grandpa", "reed", "rocko", "sandy", "shelley",
  // Older Apple voices kept only for backwards compatibility.
  "fred", "victoria", "agnes", "bruce", "vicki",
];

/**
 * Ranked. Lower is better.
 *
 * Daniel is the received-pronunciation voice most people associate with
 * cricket commentary; Rishi is Apple's Indian English male and is the better
 * fit for a Rawalpindi ground. Enhanced and Premium variants are dramatically
 * better than the compact ones and are preferred wherever installed.
 */
const RANK_MALE = [
  "rishi", "daniel", "oliver", "alex", "aaron", "arthur", "gordon",
  "google uk english male", "google english male", "microsoft ryan",
  "microsoft guy", "microsoft george", "lee", "tom", "nathan",
];
const RANK_FEMALE = [
  "samantha", "veena", "karen", "serena", "kate", "moira", "tessa", "fiona",
  "google uk english female", "google us english", "microsoft sonia",
  "microsoft aria", "ava", "allison", "susan", "nicky", "matilda",
];

export type Gender = "male" | "female";

function isNovelty(v: SpeechSynthesisVoice): boolean {
  const n = v.name.toLowerCase();
  return NOVELTY.some((bad) => n === bad || n.startsWith(bad + " ") || n.startsWith(bad + "("));
}

/**
 * Quality tiers, and why they matter more than which voice you pick.
 *
 * Apple ships each voice in up to three builds. The default "compact" one is
 * the robotic one. "Enhanced" is a large download and noticeably better.
 * "Premium" is the best available to any third party and is close to Siri.
 *
 * Siri's own voice is not obtainable. Apple withholds it from the Web Speech
 * API and, as of this writing, from AVSpeechSynthesisVoice as well — a native
 * iOS app cannot use it either. There is no workaround; the gap between
 * Premium and Siri is Apple's to close.
 */
export type Tier = "premium" | "enhanced" | "compact";

export function tierOf(v: SpeechSynthesisVoice): Tier {
  const n = v.name.toLowerCase();
  if (n.includes("premium")) return "premium";
  if (n.includes("enhanced") || n.includes("neural") || n.includes("natural")) return "enhanced";
  return "compact";
}

/** The best tier installed for a language, so the UI can prompt honestly. */
export function bestTier(lang: Lang): Tier {
  const vs = voicesFor(lang);
  if (vs.some((v) => tierOf(v) === "premium")) return "premium";
  if (vs.some((v) => tierOf(v) === "enhanced")) return "enhanced";
  return "compact";
}


function rankIn(list: string[], v: SpeechSynthesisVoice): number {
  const n = v.name.toLowerCase();
  for (let i = 0; i < list.length; i++) {
    if (n.startsWith(list[i]!)) return i;
  }
  return 999;
}

export function genderOf(v: SpeechSynthesisVoice): Gender | null {
  if (rankIn(RANK_MALE, v) < 999) return "male";
  if (rankIn(RANK_FEMALE, v) < 999) return "female";
  return null;
}

export function supported(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

/** Voices arrive asynchronously on Chromium. Poll until they do, then stop. */
export function loadVoices(onReady?: (v: SpeechSynthesisVoice[]) => void): void {
  if (!supported()) return;
  const grab = () => {
    const v = window.speechSynthesis.getVoices();
    if (v.length) {
      voices = v;
      onReady?.(v);
      return true;
    }
    return false;
  };
  if (grab()) return;
  window.speechSynthesis.onvoiceschanged = () => { grab(); };
  // Safari sometimes never fires the event; a few polls cost nothing.
  let tries = 0;
  const t = setInterval(() => { if (grab() || ++tries > 20) clearInterval(t); }, 250);
}

/**
 * Voices worth offering, best first.
 *
 * Novelty and Eloquence voices are removed entirely rather than ranked low —
 * there is no situation in which a cricket score should be read by Bubbles.
 */
export function voicesFor(lang: Lang, gender?: Gender): SpeechSynthesisVoice[] {
  // The locale preference is strong for Hindi and marginal for English. A
  // Hindi line read by a British voice is wrong in a way an American reading
  // English simply is not — so en-GB and en-US rank equally, while hi and en-IN
  // rank far above en-GB for Hinglish.
  const want = lang === "hi"
    ? ["hi", "ur", "en-in", "en-gb", "en"]
    : ["en"];
  const langScore = (v: SpeechSynthesisVoice) => {
    const l = v.lang.toLowerCase().replace("_", "-");
    for (let i = 0; i < want.length; i++) if (l.startsWith(want[i]!)) return i;
    return 99;
  };

  return voices
    .filter((v) => !isNovelty(v))
    .filter((v) => langScore(v) < 99)
    .filter((v) => (gender ? genderOf(v) === gender : true))
    .sort((a, b) => {
      // Explicit ordering rather than weighted arithmetic, because the weights
      // are impossible to reason about: an Enhanced build of a first-choice
      // voice was beating a Premium build of a second-choice one, when the tier
      // is the thing people actually hear.
      // For Hindi the locale comes first: a compact Indian English voice reads
      // Hinglish better than a Premium British one.
      if (lang === "hi") {
        const l = langScore(a) - langScore(b);
        if (l !== 0) return l;
      }

      const tier = { premium: 0, enhanced: 1, compact: 2 } as const;
      const t = tier[tierOf(a)] - tier[tierOf(b)];
      if (t !== 0) return t;

      const rank = (v: SpeechSynthesisVoice) => {
        const g = genderOf(v);
        return g === "male" ? rankIn(RANK_MALE, v)
             : g === "female" ? rankIn(RANK_FEMALE, v) : 500;
      };
      const r = rank(a) - rank(b);
      if (r !== 0) return r;

      return langScore(a) - langScore(b);
    });
}

/** The single best voice for a language and gender, or null if none exists. */
export function bestVoice(lang: Lang, gender: Gender): SpeechSynthesisVoice | null {
  return voicesFor(lang, gender)[0] ?? voicesFor(lang)[0] ?? null;
}


export function getGender(lang: Lang): Gender {
  try {
    const g = localStorage.getItem(`orbit.gender.${lang}`);
    if (g === "male" || g === "female") return g;
  } catch { /* private mode */ }
  return "male";
}

export function setVoice(lang: Lang, voiceURI: string | null): void {
  preferred = { ...preferred, [lang]: voiceURI };
  try { localStorage.setItem(`orbit.voice.${lang}`, voiceURI ?? ""); } catch { /* private mode */ }
}

export function getVoice(lang: Lang): string | null {
  if (preferred[lang]) return preferred[lang];
  try {
    const v = localStorage.getItem(`orbit.voice.${lang}`);
    if (v) { preferred = { ...preferred, [lang]: v }; return v; }
  } catch { /* private mode */ }
  return null;
}

/**
 * Must be called from inside a user gesture, once. Speaking a single space
 * satisfies the autoplay rule without the user hearing anything.
 */
export function unlock(): void {
  if (!supported() || unlocked) return;
  try {
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    window.speechSynthesis.speak(u);
    unlocked = true;
  } catch { /* nothing to do */ }
}


export interface SpeakOptions {
  lang?: Lang;
  /** Commentary sits a touch above natural pace; a ground PA is brisk. */
  rate?: number;
  pitch?: number;
  volume?: number;
  /** Drop anything queued and say this now — for a wicket. */
  interrupt?: boolean;
}

/**
 * Say a line.
 *
 * Utterances queue rather than overlap, which is right for ball-by-ball: the
 * previous ball finishes before the next begins. A wicket can jump the queue.
 */
export function say(text: string, opts: SpeakOptions = {}): void {
  if (!supported() || !text.trim()) return;
  const lang: Lang = opts.lang ?? "en";
  const synth = window.speechSynthesis;

  if (opts.interrupt) synth.cancel();
  if (synth.pending && queued > 6) { synth.cancel(); queued = 0; }

  const u = new SpeechSynthesisUtterance(shape(text));
  const uri = getVoice(lang);
  const pool = voicesFor(lang);
  const chosen = (uri ? pool.find((v) => v.voiceURI === uri) : null)
    ?? bestVoice(lang, getGender(lang))
    ?? pool[0];
  if (chosen) { u.voice = chosen; u.lang = chosen.lang; }
  else u.lang = lang === "hi" ? "hi-IN" : "en-GB";

  // Commentary read at 1.05 sounds rushed and mechanical. Slightly under
  // natural pace, with the pitch lifted a touch on a boundary and dropped on a
  // wicket, is what stops it sounding like a station announcement.
  const excited = /^(SIX|FOUR)/.test(text);
  const grave = /(WICKET|BOWLED|CAUGHT|LBW|RUN OUT|STUMPED|HIT WICKET|BOLD|CATCH)/.test(text);
  u.rate = opts.rate ?? (excited ? 1.02 : grave ? 0.94 : 0.97);
  u.pitch = opts.pitch ?? (excited ? 1.08 : grave ? 0.92 : 1);
  u.volume = opts.volume ?? 1;

  queued++;
  u.onend = () => { queued = Math.max(0, queued - 1); };
  u.onerror = () => { queued = Math.max(0, queued - 1); };
  synth.speak(u);
}

let queued = 0;

export function stop(): void {
  if (!supported()) return;
  window.speechSynthesis.cancel();
  queued = 0;
}

/**
 * Strip what reads badly aloud.
 *
 * "1.4" becomes "one point four" in most engines, which is wrong for an over;
 * scorecard shorthand like "2.0-0-13-1" is unreadable. Commentary text avoids
 * both, but a caller might pass anything.
 */
/**
 * Shape the text for speech.
 *
 * Engines take their rhythm from punctuation, so an em dash read as nothing
 * runs two clauses together. Commas and full stops are what make a line breathe
 * rather than arrive in one flat burst.
 */
function shape(s: string): string {
  return s
    .replace(/\s*—\s*/g, ", ")        // em dash reads as a run-on otherwise
    .replace(/\s*·\s*/g, ", ")
    .replace(/!\s*/g, "! ")            // a beat after an exclamation
    .replace(/(\d)\/(\d)/g, "$1 for $2")  // 48/2 -> "48 for 2"
    .replace(/\bov\b/gi, "overs")
    .replace(/\bRR\b/g, "run rate")
    .replace(/\s+/g, " ")
    .trim();
}
